import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import type { IconName } from '../components/ui/Icon';

/**
 * Amministrazione v2: le sei pagine, i loro indirizzi e la traduzione dei
 * vecchi `/admin?tab=…`.
 *
 * Fonte unica per Sidebar (sottovoci), Situazione (blocco "Le altre pagine"
 * del telefono), redirect dei vecchi link e link sparsi nel resto dell'app.
 * Specifica: os-planning/amministrazione-v2/SPEC.md (Architettura) e
 * r-proposta-claude.md § 1.3.
 *
 * Le viste dentro una pagina stanno in `?vista=` (il valore predefinito non
 * compare nell'URL, come fa `useUrlState`).
 */

export type AdminPageKey = 'situazione' | 'fatture' | 'spese' | 'fisco' | 'dipendenti' | 'banca';

export interface AdminPageDef {
  key: AdminPageKey;
  to: string;
  label: string;
  icon: IconName;
  /** Riga sotto il nome, nel blocco "Le altre pagine" del telefono. */
  sub: string;
  /** Livello minimo sul modulo AMMINISTRAZIONE (default READ). */
  minLevel?: PermissionLevel;
}

/**
 * `sub` segue la regola etichette (SPEC, 24/9): letta da sola, senza il nome
 * della pagina accanto, deve dire cosa c'è dentro. Niente "documenti", niente
 * "da controllare": le cose con il loro nome.
 */
export const ADMIN_PAGES: AdminPageDef[] = [
  { key: 'situazione', to: '/admin',            label: 'Situazione', icon: 'wallet',  sub: 'Soldi in banca, incassi e pagamenti dei prossimi mesi' },
  { key: 'fatture',    to: '/admin/fatture',    label: 'Fatture',    icon: 'invoice', sub: 'Fatture da incassare dai clienti, fatture emesse e preventivi' },
  { key: 'spese',      to: '/admin/spese',      label: 'Spese',      icon: 'receipt', sub: 'Fatture dei fornitori e spese fisse da pagare' },
  { key: 'fisco',      to: '/admin/fisco',      label: 'Fisco',      icon: 'percent', sub: 'Calendario delle tasse, F24 e IVA da versare' },
  { key: 'dipendenti', to: '/admin/dipendenti', label: 'Dipendenti', icon: 'user',    sub: 'Stipendi e TFR delle persone in busta paga', minLevel: PermissionLevel.ADMIN },
  { key: 'banca',      to: '/admin/banca',      label: 'Banca',      icon: 'swap',    sub: 'Movimenti del conto e bonifici da abbinare alle fatture' },
];

/**
 * L'utente vede questa pagina? READ sul modulo AMMINISTRAZIONE per tutte,
 * ADMIN per Dipendenti (stipendi e compensi: SPEC, riga Dipendenti).
 */
export function canSeeAdminPage(
  user: { permissions: Array<{ module: string; level: string }> } | null | undefined,
  page: AdminPageKey | AdminPageDef,
): boolean {
  if (!user) return false;
  const def = typeof page === 'string' ? ADMIN_PAGES.find((p) => p.key === page) : page;
  if (!def) return false;
  const lvl = (user.permissions.find((p) => p.module === ModuleKey.AMMINISTRAZIONE)?.level ?? PermissionLevel.NONE) as PermissionLevel;
  return hasAtLeast(lvl, def.minLevel ?? PermissionLevel.READ);
}

export const ADMIN_PATH: Record<AdminPageKey, string> = Object.fromEntries(
  ADMIN_PAGES.map((p) => [p.key, p.to]),
) as Record<AdminPageKey, string>;

/** Le viste di ogni pagina: la prima è la predefinita. */
export const FATTURE_VISTE = ['incassare', 'documenti', 'preventivi'] as const;
export const SPESE_VISTE = ['dapagare', 'fisse', 'pagate'] as const;
export const FISCO_VISTE = ['scadenze', 'f24', 'iva', 'imposte', 'rate', 'altri'] as const;
export const BANCA_VISTE = ['controllare', 'movimenti', 'commercialista'] as const;

export type FattureVista = (typeof FATTURE_VISTE)[number];
export type SpeseVista = (typeof SPESE_VISTE)[number];
export type FiscoVista = (typeof FISCO_VISTE)[number];
export type BancaVista = (typeof BANCA_VISTE)[number];

/**
 * Le etichette a schermo delle viste (le linguette `page-tabs`). Le chiavi di
 * `?vista=` restano quelle sopra: cambiano solo le parole.
 *
 * REGOLA ETICHETTE (SPEC, 24/9): ogni etichetta nomina la cosa che contiene.
 * Chi legge solo la linguetta, senza il resto della pagina, deve capire di
 * cosa si parla: "Bonifici da abbinare", non "Da controllare"; "Spese già
 * pagate", non "Pagate". La tabella dei nomi sta in SPEC.md.
 *
 * Le pagine (FatturePage, SpesePage, FiscoPage, BancaPage) e i link che
 * nominano una vista leggono da qui: le parole non si riscrivono a mano.
 * Le viste di Fisco hanno gli stessi nomi dei gruppi di tasse
 * (TAX_GROUP_LABEL di @masamune/shared), che compaiono anche nelle righe.
 */
export const VISTA_LABEL: {
  fatture: Record<FattureVista, string>;
  spese: Record<SpeseVista, string>;
  fisco: Record<FiscoVista, string>;
  banca: Record<BancaVista, string>;
} = {
  fatture: {
    incassare: 'Fatture da incassare',
    // Proposta di Lorenzo (24/9): era "Documenti di vendita".
    documenti: 'Fatture emesse da noi',
    preventivi: 'Preventivi',
  },
  spese: {
    dapagare: 'Spese da pagare',
    fisse: 'Spese fisse',
    pagate: 'Spese già pagate',
  },
  fisco: {
    scadenze: 'Calendario delle tasse',
    f24: 'F24 dei dipendenti',
    iva: 'IVA',
    imposte: 'Imposte della società (IRES e IRAP)',
    rate: 'Debiti a rate',
    altri: 'Altri versamenti al fisco',
  },
  banca: {
    controllare: 'Bonifici da abbinare',
    movimenti: 'Movimenti del conto',
    commercialista: 'Da comunicare al commercialista',
  },
};

/**
 * Costruisce un indirizzo di pagina con i suoi parametri. I valori vuoti non
 * entrano, e nemmeno la vista predefinita: l'URL resta pulito.
 */
export function adminHref(page: AdminPageKey, params: Record<string, string | null | undefined> = {}): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v);
  const defaults: Partial<Record<AdminPageKey, string>> = {
    fatture: FATTURE_VISTE[0], spese: SPESE_VISTE[0], fisco: FISCO_VISTE[0], banca: BANCA_VISTE[0],
  };
  if (sp.get('vista') && sp.get('vista') === defaults[page]) sp.delete('vista');
  const qs = sp.toString();
  return `${ADMIN_PATH[page]}${qs ? `?${qs}` : ''}`;
}

/** Scorciatoie per i link che si ripetono nell'app. */
export const adminLinks = {
  /** Una fattura da incassare, cercata per numero. */
  fattura: (numero?: string | null) => adminHref('fatture', { q: numero ?? undefined }),
  documenti: () => adminHref('fatture', { vista: 'documenti' }),
  preventivi: () => adminHref('fatture', { vista: 'preventivi' }),
  spese: () => adminHref('spese'),
  fornitori: () => adminHref('spese', { per: 'fornitore' }),
  fisco: () => adminHref('fisco'),
  banca: () => adminHref('banca'),
  movimenti: () => adminHref('banca', { vista: 'movimenti' }),
};

/**
 * `/admin?tab=…` → indirizzo nuovo, con i parametri tradotti (proposta § 1.3).
 * Restituisce null se nell'URL non c'è un `tab` (si resta sulla Situazione).
 *
 * Serve per sempre: le notifiche già salvate nel database portano ancora i
 * vecchi link (es. `link: '/admin?tab=incassi'`).
 */
export function legacyAdminUrl(search: URLSearchParams | string): string | null {
  const sp = typeof search === 'string' ? new URLSearchParams(search) : search;
  const tab = sp.get('tab');
  if (!tab) return null;
  const pick = (...keys: string[]) => Object.fromEntries(keys.map((k) => [k, sp.get(k) ?? undefined]));
  // "Anni scorsi" era un interruttore di tutta la pagina: resta acceso dove
  // esiste ancora il filtro "Anche prima del 2026".
  const storico = sp.get('storico') === '1' ? '1' : undefined;

  switch (tab) {
    case 'panoramica':
      return ADMIN_PATH.situazione;
    case 'incassi':
      // Lo stato (aperte, incassate, tutte, cestinate) resta un filtro della
      // vista "Da incassare", che lo legge ancora da `status`.
      return adminHref('fatture', { ...pick('q', 'status'), storico });
    case 'uscite': {
      const pagate = sp.get('status') === 'pagate';
      return adminHref('spese', { vista: pagate ? 'pagate' : undefined, q: sp.get('q') ?? undefined, storico });
    }
    case 'fornitori':
      return adminHref('spese', { per: 'fornitore', storico });
    case 'fisco':
      return ADMIN_PATH.fisco;
    case 'movimenti':
      return adminHref('banca', { vista: 'movimenti', ...pick('acc', 'from', 'to', 'side', 'unmatched', 'q'), storico });
    case 'riconciliazione':
      return ADMIN_PATH.banca;
    case 'chiuse-qui':
      return adminHref('banca', { vista: 'commercialista', q: sp.get('q') ?? undefined });
    case 'documenti': {
      const type = sp.get('type');
      if (type === 'quote') return adminHref('fatture', { vista: 'preventivi', ...pick('status', 'q'), storico });
      return adminHref('fatture', { vista: 'documenti', ...pick('type', 'status', 'q'), storico });
    }
    default:
      return ADMIN_PATH.situazione;
  }
}
